import React, { SyntheticEvent, useRef, useState } from 'react'
import ReactModal, { Props as ReactModalProps } from 'react-modal'

import {
  Box,
  FontFamily,
  JustifyContent,
  Text,
  withCitrusTheme,
  TextAlign,
  Color,
  Icon,
  FlexDirection,
  Display,
  IconVariant,
  FontWeight,
} from '@gousto-internal/citrus-react'

import { RecipeAlternativeOptions } from './RecipeAlternativeOptions'
import { useClickOutside } from './useClickOutside'
import { queueTrackingEvent } from '@library/tracking-integration'
import type {
  TrackRecipeAlternativeOptionsMenuOpen,
  TrackRecipeAlternativeOptionsMenuSwapRecipes,
} from './SwapAlternativeOptions'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'
import { CollectionUUID, Recipe, RecipeCoreId } from '@library/menu-service'

const Drawer = withCitrusTheme(Box, () => ({
  backgroundColor: Color.White,
  position: 'fixed',
  top: 'auto',
  bottom: '65px',
  width: '100vw',
  minHeight: 'auto',
  zIndex: '100',
  left: '0',
  [`@media (max-width: '420px')`]: {
    transform: 'translate(-50%, 0)',
  },
}))

const DrawerBody: React.FC = withCitrusTheme(Box, () => ({
  textAlign: 'left',
  margin: '0',
  padding: '1rem',
  overflowY: 'scroll',
}))

const IconCustom = withCitrusTheme(Icon, () => ({
  fontFamily: FontFamily.Bold,
}))

const DrawerHeader: React.FC = withCitrusTheme(Box, () => ({
  display: Display.Flex,
  flexDirection: FlexDirection.Row,
  justifyContent: JustifyContent.SpaceBetween,
  padding: '1rem 1rem 0rem',
}))

export type SwapAlternativeOptionsMobileProps = {
  currentRecipeCoreId: RecipeCoreId
  currentCollectionId: CollectionUUID
  selectVariant: ({
    variant,
    variantOutOfStock,
  }: {
    variant: Recipe
    variantOutOfStock: boolean
  }) => void
  alternativeOptions: AlternativeOptions
}

const overlay = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 100,
}

const content: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: 0,
  position: 'relative',
}

export const SwapAlternativeOptionsMobile: React.FC<SwapAlternativeOptionsMobileProps> = ({
  currentRecipeCoreId,
  currentCollectionId,
  selectVariant,
  alternativeOptions,
}) => {
  const [showModal, setShowModal] = useState(false)

  const selectRef = useRef(null)
  useClickOutside(selectRef, () => setShowModal(false), [showModal])

  const onCloseModal = (e: SyntheticEvent) => {
    e.stopPropagation()
    setShowModal(false)
  }
  const onClickButton = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    event.stopPropagation()
    setShowModal(!showModal)
    if (!showModal) {
      queueTrackingEvent<TrackRecipeAlternativeOptionsMenuOpen>({
        action: 'recipe-alternative-options-menu-open',
        recipeId: currentRecipeCoreId,
        collectionId: currentCollectionId,
      })
    }
  }

  return (
    <Box display="flex" justifyContent={JustifyContent.FlexEnd} style={{ marginLeft: '1rem' }}>
      <Box
        data-testid="SwapAlternativeOptionsMobileButton"
        onClick={onClickButton}
        onKeyDown={onClickButton}
        role="button"
        tabIndex={-1}
      >
        <Text fontFamily={FontFamily.Bold} color={Color.Secondary_400}>
          Options
        </Text>
      </Box>
      {showModal && (
        <ReactModal isOpen={showModal} style={{ overlay, content }}>
          <Drawer>
            <DrawerHeader>
              <Text
                textAlign={TextAlign.Left}
                fontFamily={FontFamily.Book}
                size={5}
                fontWeight={FontWeight.SemiBold}
              >
                Options available
              </Text>
              <IconCustom
                name="close"
                size={6}
                onClick={onCloseModal}
                role="button"
                variant={IconVariant.Default}
              />
            </DrawerHeader>

            <Box ref={selectRef}>
              <DrawerBody>
                <RecipeAlternativeOptions
                  currentRecipeCoreId={currentRecipeCoreId}
                  onChangeCheckedRecipe={({
                    previousRecipeId,
                    nextRecipeId,
                  }: {
                    previousRecipeId: string
                    nextRecipeId: string
                  }) => {
                    queueTrackingEvent<TrackRecipeAlternativeOptionsMenuSwapRecipes>({
                      action: 'recipe-alternative-options-menu-swap-recipes',
                      previousRecipeId,
                      nextRecipeId,
                      collectionId: currentCollectionId,
                    })
                    setShowModal(false)
                  }}
                  alternativeOptions={alternativeOptions}
                  selectVariant={selectVariant}
                  isMobile={true}
                />
              </DrawerBody>
            </Box>
          </Drawer>
        </ReactModal>
      )}
    </Box>
  )
}
